/**
 * 汎用のファイルドロップゾーン (ドラッグ＆ドロップ +「ファイルを選ぶ」)。
 *
 * express/components/BgmDropZone.tsx の最小構成 (拡張子・MIME 判定 / ドラッグ中の強調 / hidden input) を
 * 受け付け形式を props にして切り出したもの。アップロード処理や結果表示は呼び出し側が持つ。
 */
import { useCallback, useRef, useState, type ReactNode } from 'react';
import styles from './FileDropZone.module.css';

export interface FileDropZoneProps {
  /** 受け付ける拡張子 (小文字・ドット付き。例: ['.md', '.txt'])。input の accept にも使う */
  acceptExtensions: readonly string[];
  /** 拡張子が合わないときに追加で受け付ける MIME。省略時は拡張子だけで判定する */
  acceptMimeTypes?: readonly string[];
  /** 受け付けたファイル */
  onFileSelect: (file: File) => void;
  /** 受け付けない形式のファイルが来た (トースト等で知らせる) */
  onReject?: (file: File) => void;
  /** 処理中 (スピナー表示・操作不可) */
  busy?: boolean;
  busyLabel?: string;
  disabled?: boolean;
  /** ドロップゾーンの説明文 (省略時「ここにファイルをドラッグ＆ドロップ」) */
  label?: ReactNode;
  chooseLabel?: string;
  className?: string;
  'data-testid'?: string;
}

export function isFileAccepted(
  file: { name: string; type?: string },
  acceptExtensions: readonly string[],
  acceptMimeTypes?: readonly string[],
): boolean {
  const lower = file.name.toLowerCase();
  if (acceptExtensions.some((ext) => lower.endsWith(ext.toLowerCase()))) return true;
  return !!acceptMimeTypes && !!file.type && acceptMimeTypes.includes(file.type);
}

export function FileDropZone({
  acceptExtensions,
  acceptMimeTypes,
  onFileSelect,
  onReject,
  busy = false,
  busyLabel = '読み込み中…',
  disabled = false,
  label = 'ここにファイルをドラッグ＆ドロップ',
  chooseLabel = 'ファイルを選ぶ',
  className,
  'data-testid': testId,
}: FileDropZoneProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const inactive = disabled || busy;

  const handleFile = useCallback((file: File | undefined) => {
    if (!file) return;
    if (isFileAccepted(file, acceptExtensions, acceptMimeTypes)) onFileSelect(file);
    else onReject?.(file);
  }, [acceptExtensions, acceptMimeTypes, onFileSelect, onReject]);

  return (
    <div
      className={`${styles.zone} ${isDragOver ? styles.zoneActive : ''} ${disabled ? styles.zoneDisabled : ''} ${className ?? ''}`}
      data-testid={testId}
      aria-disabled={disabled || undefined}
      onDragOver={(e) => {
        e.preventDefault();
        if (inactive) return;
        e.dataTransfer.dropEffect = 'copy';
        setIsDragOver(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setIsDragOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOver(false);
        if (inactive) return;
        handleFile(e.dataTransfer.files?.[0]);
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={[...acceptExtensions, ...(acceptMimeTypes ?? [])].join(',')}
        style={{ display: 'none' }}
        disabled={inactive}
        onChange={(e) => {
          handleFile(e.target.files?.[0]);
          // 同じファイルを選び直しても onChange が発火するようにリセットする
          e.target.value = '';
        }}
      />
      {busy ? (
        <span className={styles.busy} role="status">
          <span className={styles.spinner} aria-hidden="true" />
          {busyLabel}
        </span>
      ) : (
        <>
          <span>{label}</span>
          <button
            type="button"
            className={styles.chooseButton}
            disabled={inactive}
            onClick={() => inputRef.current?.click()}
          >
            {chooseLabel}
          </button>
        </>
      )}
    </div>
  );
}
